import React, { useState, useMemo, useEffect } from "react";
import "./ObjectivesProgression.scss";
import StudentProgression from "../../StudentProgression/StudentProgression";
import {
    Objective,
    Student,
    TestResult,
} from "../../../../../interfaces/Dashboard";
import ProgressionBar from "../../ProgressionOverview/ProgressionTable/ProgressionBar/ProgressionBar";
import { AIType } from "@evidenceb/ai-handler";

import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import { IntlShape, defineMessages, useIntl } from "react-intl";
import TestProgressionResults from "../../TestProgressionResults/TestProgressionResults";
import SimpleBar from "simplebar-react";
import Case from "case";
import { commonMessages } from "../../../../../utils/messages";
import { getObjectiveById } from "../../../../../utils/dataRetrieval";
import { useData } from "../../../../../hooks/useData";
import { Data } from "../../../../../interfaces/Data";
import classNames from "classnames";
import { changeObjective, isDisabled } from "../../StudentProgression/utils";
import { calculateAssessment, evaluateAlert } from "../../api/utils";
import { useCachedStudentsAlerts } from "../../ProgressionOverview/useStudentsAlerts";

interface Props {
    student: Student;
}
const ObjectivesProgression = ({ student }: Props) => {
    const data = useData();
    const intl = useIntl();
    const [selectedObjective, setSelectedObjective] = useState<
        Objective | undefined
    >();
    const [displayTestResults, setDisplayTestResults] = useState<boolean>();
    const testResult = useMemo<undefined | TestResult>(
        () =>
            student.testsResults?.find(
                (test) =>
                    test.type === AIType.BanditManchot || AIType.AdaptiveTest
            ),
        [student]
    );

    const objectives = student.objectives;

    const objectiveIndex = student.objectives.findIndex((o) => {
        if (!selectedObjective) return -1;
        return o.id === selectedObjective.id;
    });

    const studentsAlerts = useCachedStudentsAlerts();

    useEffect(() => {
        if (!testResult || testResult.status === "none") {
            setSelectedObjective(student.objectives[0]);
            setDisplayTestResults(false);
        }

        if (
            testResult?.status === "inProgress" ||
            testResult?.status === "completed"
        ) {
            setSelectedObjective(undefined);
            setDisplayTestResults(true);
        }
    }, [student.objectives, testResult]);

    return (
        <div className="objectives-progression">
            <nav className="objectives-progression__nav">
                {testResult && (
                    <button
                        className={classNames("initial-test", {
                            "--active": displayTestResults,
                        })}
                        onClick={() => {
                            setDisplayTestResults(true);
                            setSelectedObjective(undefined);
                        }}
                        onFocus={() => {
                            setDisplayTestResults(true);
                            setSelectedObjective(undefined);
                        }}
                        key="test-result"
                    >
                        <span aria-hidden={true}>
                            {intl.formatMessage(messages.initialTest)}
                        </span>
                        <span className="objectives-progression__nav__progression-wrapper">
                            <ProgressionBar
                                className="objectives-progression__nav__progression-bar"
                                aria-hidden={true}
                                status={testResult?.status ?? "none"}
                            />
                        </span>
                        <VisuallyHidden as="span">
                            {intl.formatMessage(messages.openTestResult)}
                        </VisuallyHidden>
                    </button>
                )}
                {student.objectives.map((objective, index) => {
                    const assessment = calculateAssessment(objective);
                    const studentAlerts = studentsAlerts?.length
                        ? studentsAlerts.find((s) => s.id === student.id)
                        : undefined;
                    const isBlocked = !!studentAlerts?.alerts.find(
                        (alert) => alert.objective === objective.id
                    );
                    return (
                        <button
                            className={classNames({
                                "--active":
                                    selectedObjective &&
                                    objective.id === selectedObjective.id,
                                "first-objective": index === 0,
                                "last-objective":
                                    index === student.objectives.length - 1,
                            })}
                            onClick={() => {
                                setDisplayTestResults(false);
                                setSelectedObjective(objective);
                            }}
                            onFocus={() => {
                                setDisplayTestResults(false);
                                setSelectedObjective(objective);
                            }}
                            key={objective.id}
                        >
                            <span aria-hidden={true}>{`O.${index + 1}`}</span>
                            <span className="objectives-progression__nav__progression-wrapper">
                                <ProgressionBar
                                    className="objectives-progression__nav__progression-bar"
                                    aria-hidden={true}
                                    status={objective.status}
                                    alert={evaluateAlert(isBlocked, assessment)}
                                />
                            </span>
                            <VisuallyHidden>
                                {getObjectiveName(
                                    index,
                                    objective.id,
                                    data,
                                    intl
                                )}
                            </VisuallyHidden>
                        </button>
                    );
                })}
            </nav>
            {selectedObjective && (
                <StudentProgression
                    studentId={student.id}
                    className="objectives-progression__progression"
                    objective={selectedObjective}
                    objectiveIndex={objectiveIndex}
                    onGoToNextObjective={
                        isDisabled("next", objectiveIndex, objectives)
                            ? undefined
                            : () => {
                                  const newObjective = changeObjective(
                                      "next",
                                      objectiveIndex,
                                      objectives
                                  );
                                  if (newObjective)
                                      setSelectedObjective(newObjective);
                              }
                    }
                    onGoToPreviousObjective={
                        isDisabled("prev", objectiveIndex, objectives, {
                            hasInitialTest: true,
                        })
                            ? undefined
                            : () => {
                                  if (objectiveIndex === 0) {
                                      setDisplayTestResults(true);
                                      setSelectedObjective(undefined);
                                  } else {
                                      const newObjective = changeObjective(
                                          "prev",
                                          objectiveIndex,
                                          objectives
                                      );
                                      if (newObjective)
                                          setSelectedObjective(newObjective);
                                  }
                              }
                    }
                    legendPosition="bottom"
                />
            )}
            {testResult && displayTestResults && (
                <SimpleBar style={{ maxHeight: "580px" }}>
                    <TestProgressionResults
                        className="objectives-progression__test-result"
                        testResult={testResult}
                        studentId={student.id}
                        onGoingNext={() => {
                            setDisplayTestResults(false);
                            setSelectedObjective(student.objectives[0]);
                        }}
                    />
                </SimpleBar>
            )}
        </div>
    );
};

export default ObjectivesProgression;

const getObjectiveName = (
    index: number,
    id: string,
    data: Data,
    intl: IntlShape
) => {
    return intl.formatMessage(messages.a11ySeeObjectiveProgression, {
        objectiveName: `${Case.pascal(
            intl.formatMessage(commonMessages.objectiveShort).charAt(0)
        )}${index}. ${getObjectiveById(id, data).title.short}`,
    });
};

const messages = defineMessages({
    a11ySeeObjectiveProgression: {
        id: "dashboard-a11y-seeObjectiveProgression",
        defaultMessage: "Open the progression of the objective {objectiveName}",
    },
    openTestResult: {
        id: "dashboard-openTestResultDetail",
        defaultMessage: "Open the test's detailed results",
    },
    initialTest: {
        id: "initial-test",
        defaultMessage: "Initial test",
    },
});
